import { Link } from "@tanstack/react-router";
import { formatPrice, statusLabels, type Product } from "@/data/product-types";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

export function ProductStatusBadge({ status }: { status: Product["status"] }) {
  const tone =
    status === "available"
      ? "border-forest/40 text-forest"
      : status === "previously-created"
        ? "border-walnut/40 text-walnut"
        : status === "temporarily-unavailable"
          ? "border-muted-foreground/40 text-muted-foreground"
          : "border-brass/60 text-walnut";
  // Status → badge tone mapping (should match statusLabels in src/data/products.ts).
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-2.5 py-1 text-[0.66rem] font-semibold uppercase tracking-[0.1em] before:size-1.5 before:rounded-full before:bg-current before:content-['']",
        tone,
      )}
    >
      {statusLabels[status]}
    </span>
  );
}

export function ProductCard({ product, eager = false }: { product: Product; eager?: boolean }) {
  const image = product.images[0];
  return (
    <article className="group relative flex h-full flex-col surface-card motion-craft transition-shadow hover:shadow-[var(--shadow-raise)]">
      <div className="relative overflow-hidden bg-paper">
        <img
          src={image.src}
          alt={image.alt}
          width={image.width}
          height={image.height}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className="aspect-[4/3] w-full object-cover motion-craft transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <div className="absolute left-3 top-3">
          <ProductStatusBadge status={product.status} />
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <p className="eyebrow text-[0.65rem]">{product.subcategory}</p>
        <h3 className="mt-2 font-[family-name:var(--font-display)] text-xl leading-snug">
          <Link
            to="/creations/$slug"
            params={{ slug: product.slug }}
            onClick={() => track("select_product", { item_id: product.slug })}
            className="after:absolute after:inset-0 hover:text-walnut motion-craft transition-colors"
          >
            {product.name}
          </Link>
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{product.shortDescription}</p>

        <dl className="mt-4 space-y-1 text-xs text-muted-foreground">
          <div className="flex gap-2">
            <dt className="font-semibold text-ink/70">Material</dt>
            <dd className="truncate">{product.materials.join(", ")}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-semibold text-ink/70">Price</dt>
            <dd>{formatPrice(product)}</dd>
          </div>
        </dl>

        <p className="mt-5 border-t border-hairline pt-4 text-sm font-semibold text-ink">View details</p>
      </div>
    </article>
  );
}

export function RequestSimilarLink({
  product,
  className,
}: {
  product: Pick<Product, "slug" | "name">;
  className?: string;
}) {
  return (
    <Link
      to="/design-your-piece"
      search={{ ref: product.slug }}
      onClick={() => track("request_similar_click", { item_id: product.slug })}
      className={cn(
        "tap-safe relative z-10 inline-flex items-center justify-center rounded-sm border border-ink/25 px-4 text-sm font-semibold hover:border-ink hover:bg-paper motion-craft transition-colors",
        className,
      )}
    >
      Request Something Similar
    </Link>
  );
}
